/**
 * Web-compatible localStorage implementation that matches the SQLite interface
 */
interface ILocalStorageDB {
    init(): Promise<void>;
    setItem(key: string, value: string): Promise<void>;
    getItem(key: string): string | null;
    removeItem(key: string): Promise<void>;
    clear(): Promise<void>;
}

/**
 * Web localStorage service for key-value operations
 * Provides the same interface as the React Native SQLite version
 */
class LocalStorageDB implements ILocalStorageDB {
    private prefix = 'mkf_'; // Prefix to avoid conflicts with other localStorage usage

    /**
     * Initialize localStorage (no-op for web, but kept for interface compatibility)
     */
    async init(): Promise<void> {
        // No initialization needed for web localStorage
    }

    /**
     * Save key-value pair to localStorage
     */
    async setItem(key: string, value: string): Promise<void> {
        try {
            localStorage.setItem(this.prefix + key, value);
        } catch (error) {
            console.error('Failed to save to localStorage:', error);
            // Handle quota exceeded error
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                console.error('localStorage quota exceeded');
            }
        }
    }

    /**
     * Get value by key from localStorage
     */
    getItem(key: string): string | null {
        try {
            return localStorage.getItem(this.prefix + key);
        } catch (error) {
            console.error(`Failed to get item from localStorage:`, error);
            return null;
        }
    }

    /**
     * Remove item by key from localStorage
     */
    async removeItem(key: string): Promise<void> {
        try {
            localStorage.removeItem(this.prefix + key);
        } catch (error) {
            console.error(`Failed to remove item from localStorage:`, error);
        }
    }

    /**
     * Clear all items with our prefix from localStorage
     */
    async clear(): Promise<void> {
        try {
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.prefix)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
            console.log(`LocalStorageDB cleared successfully`);
        } catch (error) {
            console.error(`Failed to clear localStorage:`, error);
        }
    }
}

// Export singleton instance
export const localStorageDB = new LocalStorageDB();